import React, { useState, useEffect } from 'react';
import axios from 'axios';

const Members = () => {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordMember, setPasswordMember] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  
  // Cache for member search suggestions (name + phone)
  const [memberCache, setMemberCache] = useState([]);
  
  const getDefaultFormData = () => ({
    name: '',
    phone: '',
    email: '',
    address: '',
    startDate: new Date().toISOString().split('T')[0],
    expiry: '',
    tenure: 1,
    renewalDue: ''
  });

  const [formData, setFormData] = useState(getDefaultFormData());

  useEffect(() => {
    fetchMembers();
    loadMemberCache(); // Load cache for search suggestions
  }, []);

  // Auto-dismiss success message after 3 seconds
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(''), 3000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  const fetchMembers = async (search = '') => {
    try {
      setIsSearching(true);
      const token = localStorage.getItem('token');
      const limit = search ? 100 : 30; // Load more when searching
      const url = search 
        ? `/api/members?limit=${limit}&search=${encodeURIComponent(search)}`
        : `/api/members?limit=${limit}`;
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMembers(response.data.members || []);
      if (search) setHasSearched(true);
    } catch (error) {
      console.error('Failed to fetch members:', error);
    } finally {
      setLoading(false);
      setIsSearching(false);
    }
  };

  // Load member cache for search suggestions (just names and phones)
  const loadMemberCache = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/members?limit=1000', {
        headers: { Authorization: `Bearer ${token}` }
      });
      const cache = (response.data.members || []).map(m => ({
        id: m.id,
        name: m.name,
        phone: m.phone
      }));
      setMemberCache(cache);
    } catch (error) {
      console.error('Failed to load member cache:', error);
    }
  };

  // Handle search button click
  const handleSearch = () => {
    if (searchTerm.trim()) {
      fetchMembers(searchTerm.trim());
    }
  };

  // Handle Enter key in search
  const handleSearchKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  // Clear search and show recent
  const clearSearch = () => {
    setSearchTerm('');
    setHasSearched(false);
    fetchMembers();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    
    setIsSubmitting(true);
    const token = localStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}` };

    try {
      if (editingMember) {
        await axios.put(`/api/members/${editingMember.id}`, formData, { headers });
        setSuccessMessage('Member updated successfully!');
      } else {
        await axios.post('/api/members', formData, { headers });
        setSuccessMessage('Member added successfully!');
      }
      fetchMembers();
      closeModal();
    } catch (error) {
      alert(error.response?.data?.message || 'Operation failed');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this member?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`/api/members/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuccessMessage('Member deleted successfully!');
      fetchMembers();
    } catch (error) {
      alert('Failed to delete member');
    }
  };

  const openPasswordModal = (member) => {
    setPasswordMember(member);
    setNewPassword('');
    setShowPasswordModal(true);
  };

  const handleSetPassword = async (e) => {
    e.preventDefault();
    if (isSubmitting || !newPassword) return;
    
    setIsSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`/api/members/${passwordMember.id}/password`, 
        { password: newPassword },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSuccessMessage(`Password set for ${passwordMember.name}`);
      setShowPasswordModal(false);
      setPasswordMember(null);
      setNewPassword('');
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to set password');
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toISOString().split('T')[0];
  };

  const openModal = (member = null) => {
    if (member) {
      setEditingMember(member);
      setFormData({
        name: member.name,
        phone: member.phone,
        email: member.email || '',
        address: member.address || '',
        startDate: formatDate(member.startDate),
        expiry: formatDate(member.expiry),
        tenure: member.tenure || 1,
        renewalDue: formatDate(member.renewalDue)
      });
    } else {
      setEditingMember(null);
      setFormData(getDefaultFormData());
    }
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingMember(null);
  };

  // Filter members based on search term (for display, actual search is server-side)
  const filteredMembers = members;

  // Get search suggestions from cache
  const searchSuggestions = searchTerm.length >= 2 
    ? memberCache.filter(m => 
        m.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        m.phone?.includes(searchTerm)
      ).slice(0, 5)
    : [];

  if (loading) return <div className="loading">Loading members...</div>;

  return (
    <div>
      <div className="page-header">
        <h1>Members</h1>
        <button className="btn btn-success" onClick={() => openModal()}>
          + Add Member
        </button>
      </div>

      {/* Success Message Popup */}
      {successMessage && (
        <div className="success-popup" style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          background: '#4CAF50',
          color: 'white',
          padding: '15px 25px',
          borderRadius: '5px',
          boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
          zIndex: 1000,
          animation: 'fadeIn 0.3s'
        }}>
          {successMessage}
          <button 
            onClick={() => setSuccessMessage('')}
            style={{
              marginLeft: '15px',
              background: 'none',
              border: 'none',
              color: 'white',
              cursor: 'pointer',
              fontSize: '16px'
            }}
          >×</button>
        </div>
      )}

      <div className="search-container" style={{ marginBottom: '20px', display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
        <div style={{ position: 'relative' }}>
          <input
            type="text"
            placeholder="Search by name, phone, or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyPress={handleSearchKeyPress}
            style={{
              width: '300px',
              padding: '10px 15px',
              fontSize: '14px',
              border: '1px solid #ddd',
              borderRadius: '5px',
              outline: 'none'
            }}
          />
          {/* Search suggestions dropdown */}
          {searchSuggestions.length > 0 && searchTerm.length >= 2 && (
            <div style={{
              position: 'absolute',
              top: '100%',
              left: 0,
              right: 0,
              background: 'white',
              border: '1px solid #ddd',
              borderRadius: '5px',
              boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
              zIndex: 100,
              maxHeight: '200px',
              overflowY: 'auto'
            }}>
              {searchSuggestions.map(s => (
                <div
                  key={s.id}
                  onClick={() => { setSearchTerm(s.name); handleSearch(); }}
                  style={{
                    padding: '10px 15px',
                    cursor: 'pointer',
                    borderBottom: '1px solid #eee',
                    fontSize: '14px'
                  }}
                  onMouseEnter={(e) => e.target.style.background = '#f5f5f5'}
                  onMouseLeave={(e) => e.target.style.background = 'white'}
                >
                  <strong>{s.name}</strong> - {s.phone}
                </div>
              ))}
            </div>
          )}
        </div>
        <button
          onClick={handleSearch}
          disabled={isSearching || !searchTerm.trim()}
          style={{
            padding: '10px 20px',
            background: '#4CAF50',
            color: 'white',
            border: 'none',
            borderRadius: '5px',
            cursor: searchTerm.trim() ? 'pointer' : 'not-allowed',
            opacity: searchTerm.trim() ? 1 : 0.6
          }}
        >
          {isSearching ? '🔄' : '🔍'} Search
        </button>
        {hasSearched && (
          <button
            onClick={clearSearch}
            style={{
              padding: '10px 20px',
              background: '#6c757d',
              color: 'white',
              border: 'none',
              borderRadius: '5px',
              cursor: 'pointer'
            }}
          >
            ✕ Clear
          </button>
        )}
        <span style={{ color: '#666', fontSize: '13px' }}>
          {hasSearched 
            ? `Found ${members.length} member${members.length !== 1 ? 's' : ''}`
            : `Showing last 30 members`}
        </span>
      </div>

      <div className="table-container">
        {filteredMembers.length > 0 ? (
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Phone</th>
                <th>Email</th>
                <th>Address</th>
                <th>Joining Date</th>
                <th>Start Date</th>
                <th>Expiry</th>
                <th>Tenure</th>
                <th>Renewal Due</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredMembers.map(member => (
                <tr key={member.id}>
                  <td>{member.name}</td>
                  <td>{member.phone}</td>
                  <td>{member.email}</td>
                  <td>{member.address}</td>
                  <td>{formatDate(member.joiningDate)}</td>
                  <td>{formatDate(member.startDate)}</td>
                  <td>{formatDate(member.expiry)}</td>
                  <td>{member.tenure}</td>
                  <td>{formatDate(member.renewalDue)}</td>
                  <td>
                    <span className={`status-badge status-${member.status?.toLowerCase()}`}>
                      {member.status}
                    </span>
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button className="action-btn edit" onClick={() => openModal(member)}>
                        Edit
                      </button>
                      <button 
                        className="action-btn" 
                        onClick={() => openPasswordModal(member)}
                        style={{ background: '#9c27b0', color: 'white' }}
                        title="Set member password for portal login"
                      >
                        🔑 Password
                      </button>
                      <button className="action-btn delete" onClick={() => handleDelete(member.id)}>
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="empty-state">
            <h3>{searchTerm ? 'No matching members found' : 'No members found'}</h3>
            <p>{searchTerm ? 'Try a different search term' : 'Click "Add Member" to register a new gym member'}</p>
          </div>
        )}
      </div>

      {/* Password Modal */}
      {showPasswordModal && (
        <div className="modal-overlay" onClick={() => setShowPasswordModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '400px' }}>
            <h2>🔑 Set Member Password</h2>
            <p style={{ color: '#666', marginBottom: '20px' }}>
              Set login password for <strong>{passwordMember?.name}</strong> ({passwordMember?.phone})
            </p>
            <form onSubmit={handleSetPassword}>
              <div className="form-group">
                <label>New Password</label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={e => setNewPassword(e.target.value)}
                  placeholder="Enter password (min 4 characters)"
                  minLength={4}
                  required
                  autoFocus
                />
              </div>
              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setShowPasswordModal(false)} disabled={isSubmitting}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={isSubmitting || newPassword.length < 4}>
                  {isSubmitting ? 'Setting...' : 'Set Password'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h2>{editingMember ? 'Edit Member' : 'Add New Member'}</h2>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Full Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={e => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Phone</label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={e => setFormData({ ...formData, phone: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={e => setFormData({ ...formData, email: e.target.value })}
                  placeholder="Enter email address"
                />
              </div>
              <div className="form-group">
                <label>Address</label>
                <input
                  type="text"
                  value={formData.address}
                  onChange={e => setFormData({ ...formData, address: e.target.value })}
                  placeholder="Enter address"
                />
              </div>
              <div className="form-group">
                <label>Start Date</label>
                <input
                  type="date"
                  value={formData.startDate}
                  onChange={e => setFormData({ ...formData, startDate: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Expiry Date</label>
                <input
                  type="date"
                  value={formData.expiry}
                  onChange={e => setFormData({ ...formData, expiry: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>Tenure (Months)</label>
                <input
                  type="number"
                  value={formData.tenure}
                  onChange={e => setFormData({ ...formData, tenure: parseInt(e.target.value) || 1 })}
                  min="1"
                  required
                />
              </div>
              <div className="form-group">
                <label>Renewal Due Date</label>
                <input
                  type="date"
                  value={formData.renewalDue}
                  onChange={e => setFormData({ ...formData, renewalDue: e.target.value })}
                />
              </div>
              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={closeModal} disabled={isSubmitting}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
                  {isSubmitting ? 'Processing...' : (editingMember ? 'Update Member' : 'Add Member')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Members;
